import { Global } from "@emotion/react";
import styled from "@emotion/styled";
import { breakpoints } from "@trustpilot/businessapp-patternlibrary";
import { ConstellationProvider, Locales } from "@trustpilot/constellation-components";
import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import configuration from "../configuration";
import { trackingConst } from "../constants";
import { makeStyles } from "../styles";
import { BusinessAppData, ShellProps } from "../types";
import { useAnalytics } from "../utils/analytics";
import { showBusinessUnitConsents } from "../utils/employeePermissions";
import { isTrustpilotEmployee } from "../utils/employeePermissions";
import { useHotjarTracking } from "../utils/useHotjarTracking";
import { AccountSelector } from "./accountSelector";
import BusinessAccountSelector from "./businessAccountSelector";
import BusinessUserSettings from "./businessUserSettings";
import ContentIntegrityMessages from "./contentIntegrityMessages";
import { CrowdinInContext } from "./crowdinInContext";
import { isMediumWidthViewport, isTabletDevice } from "./helpers";
import Sidebar from "./sidebar";
import { BurgerMenu } from "./sidebar/components/burgerMenu";
import { FreeTrialModal } from "./sidebar/components/freeTrialModal";
import { EligibleForUpgradeResponseBody, Trial } from "./sidebar/types/types";
import { makeMenu, getActiveItem, useIsSmall, useIsMedium } from "./sidebar/utils";
import { Simplicity } from "./simplicity";
import { StyledWrapper, StyledContent, StyledTopbar, StyledOverlay, DarkGreenTheme } from "./styled";
import { SalesforceChat } from "./supportChat/salesforce";
import UnpaidInvoiceMessages from "./unpaidInvoiceMessages";

type RefType = {
  closeAccountSelector: () => void;
};

const StyledThemeProvider = styled(DarkGreenTheme)({
  width: "calc(100% - 82px)",

  [`@media (min-width: ${breakpoints.small}px)`]: {
    width: "100%",
    minWidth: "100%",
  },
});

const Shell: React.FC<BusinessAppData & ShellProps> = ({
  businessUser,
  businessUnit,
  salesforceId,
  locale,
  launchDarkly,
  b2BAccessPermissions,
  notifications,
  businessAccounts,
  publicProfileUrl,
  selectedBusinessAccountId,
  selectedBusinessAccountBusinessUnits,
  billingInfo,
  unpaidInvoiceInfo,
  accountManager,
  productLedTrial,
  eligibility,
  complianceNotifications,
  reviewCountAwaitingReply,
  hasAppError,
  showSubMenu,
  setShowSubMenu,
  isCollapsed,
  setIsCollapsed,
  fetchedShellData,
  onboardingNotification,
}) => {
  const analytics = useAnalytics();
  const { pathname } = useLocation();
  const [overlayVisibility, toggleOverlayVisibility] = React.useState<boolean>(false);
  const [showContentIntegrityMessages, setShowContentIntegrityMessages] = React.useState<boolean>(false);

  const ref = useRef<RefType>();
  const onCloseAccountSelector = React.useCallback(() => setShowContentIntegrityMessages(true), []);

  const useSimplifiedLayout = configuration.simplifiedLayoutPages.some((p) => pathname === p);
  const isSmall = useIsSmall();
  const isLarge = useIsMedium();

  const styles = makeStyles({ useSimplifiedLayout, isCollapsed });
  const tier = {
    name: businessUnit.planName,
    expireDate: businessUnit.planExpireDate,
  };

  const [isMenuExpandOnMobile, setIsMenuExpandOnMobile] = React.useState<boolean>(false);
  const expandMenuOnMobile = React.useCallback(() => {
    setIsMenuExpandOnMobile(!isMenuExpandOnMobile);
  }, [setIsMenuExpandOnMobile, isMenuExpandOnMobile]);

  const { hasReviewInsights } = b2BAccessPermissions;

  const standardAccess =
    businessUnit.availableFeatures.includes("Insights_Sentiment") ||
    businessUnit.availableFeatures.includes("B2B2024_AppReviewInsights") ||
    launchDarkly.includes("b2b-sentiment-app-review-insights");
  const reviewInsightsVisible = hasReviewInsights && standardAccess;

  const isTpEmployee = isTrustpilotEmployee(businessUser.privileges, businessUser.email);

  const { menu } = makeMenu({
    sidebarRolePermissions: b2BAccessPermissions,
    features: businessUnit.availableFeatures,
    launchDarklyFlags: launchDarkly,
    businessUnitAccessPermissions: businessUnit.access.permissions,
    reviewInsightsVisible,
    isTrustpilotEmployee: isTpEmployee,
    modules: businessUnit.businessModules,
    reviewCountAwaitingReply,
    userRole: businessUnit.access?.role?.name,
  });

  const [newActiveItem, setNewActiveItem] = React.useState(getActiveItem(menu, pathname));
  const [navItems, setNavItems] = React.useState({
    isOpenPrimarySubmenu: "",
    isOpenSecondarySubmenu: "",
    isTertiarySubmenu: "",
  });

  const closeSubNav = React.useCallback(
    (ignoreCollapse = false) => {
      setShowSubMenu(false, ignoreCollapse);
      setNavItems({
        ...navItems,
        isOpenPrimarySubmenu: newActiveItem?.activePrimary,
        isOpenSecondarySubmenu: newActiveItem?.activeSecondary,
        isTertiarySubmenu: newActiveItem?.activeTertiary,
      });
    },
    [newActiveItem, showSubMenu, pathname],
  );

  const handleMouseLeaveWithDelay = (e) => {
    const primaryMenu = document.querySelector('[class*="sideNav-module__sidebarContainer"]');
    const subMenu = document.querySelector('[class*="sideNav-module__sidebarPrimaryMenu"]');

    setTimeout(() => {
      if ((isTabletDevice() || !isMediumWidthViewport()) && !isLarge) {
        return;
      }
      const businessUserSettingsContainer = document.getElementById("business-user-settings");
      if (!businessUserSettingsContainer) {
        return;
      }
      if (businessUserSettingsContainer.contains(e.target)) {
        // do nothing if hovering elements inside businessUserSettings component
        return;
      }
      if (
        (primaryMenu && (primaryMenu.parentNode as Element).matches(":hover")) ||
        (subMenu && (subMenu.parentNode as Element).matches(":hover"))
      ) {
        // then mouse is hovering the menu, do nothing
        return;
      }
      if (isSmall) {
        closeSubNav(false);

        // call child's function to close accountSelector:
        if (ref && ref.current && isMediumWidthViewport()) {
          ref.current.closeAccountSelector();
        }
      }
    }, 600);
  };

  // update active menu item when pathname is updated from within a microsite:
  React.useEffect(() => {
    setNewActiveItem(getActiveItem(menu, window.location.pathname));
  }, [window.location.pathname]);

  const ThemedBusinessAccountSelector = (
    <StyledThemeProvider>
      <BusinessAccountSelector
        businessAccounts={businessAccounts}
        businessUnits={selectedBusinessAccountBusinessUnits}
        currentBusinessUnit={businessUnit}
        publicProfileUrl={publicProfileUrl}
        selectedBusinessAccountId={selectedBusinessAccountId}
        showBusinessUnitConsents={showBusinessUnitConsents(businessUser.privileges, businessUser.email)}
        isTrustpilotEmployee={isTpEmployee}
        toggleOverlayVisibility={toggleOverlayVisibility}
        canManageProfile={b2BAccessPermissions.hasManageProfile}
        isMenuExpandOnMobile={isMenuExpandOnMobile}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        ref={ref}
      />
    </StyledThemeProvider>
  );

  const [showFreeTrialModal, setShowFreeTrialModal] = useState<boolean>(false);

  const [trial, setTrial] = React.useState<Trial>(null);

  React.useEffect(() => {
    const LEGACY_TRIAL_DURATION = 30;

    const currentPaidPlan = businessUnit.businessModules.find(
      (module) => module?.mainPlatformModule && !module.isTrialModule,
    );

    if (productLedTrial && !currentPaidPlan) {
      const { hasTrial, duration, subscriptionStartDate, trialState, daysLeftOnTrial } = productLedTrial;
      const trialEndDate = new Date(subscriptionStartDate || "");
      const currentDate = new Date();
      if (hasTrial && currentDate < trialEndDate) {
        setTrial({
          hasTrial,
          duration,
          subscriptionStartDate,
          trialState,
          planName: businessUnit?.planName,
          isLegacy: false,
          daysLeftOnTrial,
        });
      }
    } else if (businessUnit?.trial) {
      setTrial({
        hasTrial: !!businessUnit.trial,
        duration: LEGACY_TRIAL_DURATION,
        subscriptionStartDate: businessUnit.trial?.expirationDate,
        trialState: "TrialActive",
        planName: businessUnit.trial?.platformModuleTrial ? businessUnit.trial?.name : "",
        isLegacy: true,
      });
    }
  }, [productLedTrial]);

  const freeTrialDashboardEnabled = launchDarkly.includes("free-trial-dashboard");
  const onboardingNotificationsScheduleEnabled = launchDarkly.includes("b2b-onboarding-notifications-schedule");

  useHotjarTracking({
    trial,
    businessUserId: businessUser.id,
    fetchedShellData,
  });

  return (
    <>
      <Global styles={styles.global.app} />
      <StyledWrapper styles={styles.appShell.wrapper} useSimplifiedLayout={useSimplifiedLayout} data-appshell="true">
        <StyledContent
          styles={styles.appShell.content}
          onMouseEnter={(e) => {
            if (isTabletDevice() || !isMediumWidthViewport()) {
              return;
            }
            const businessUserSettingsContainer = document.getElementById("business-user-settings");
            if (!businessUserSettingsContainer) {
              return;
            }
            if (businessUserSettingsContainer.contains(e.target)) {
              // do nothing if hovering elements inside businessUserSettings component
              return;
            }
            setIsCollapsed(false);
            if (showSubMenu) {
              setShowSubMenu(false);
            }
          }}
          onMouseLeave={handleMouseLeaveWithDelay}
        >
          <React.Fragment>
            <StyledOverlay show={overlayVisibility} />
            <StyledTopbar id="navigation-topbar" withBackground={!useSimplifiedLayout} isCollapsed={isCollapsed}>
              {!useSimplifiedLayout && ThemedBusinessAccountSelector}
              <BusinessUserSettings
                hasLiveChatFeature={businessUnit.availableFeatures.includes("B2B_LiveSupportChat")}
                businessUser={businessUser}
                notifications={notifications}
                toggleOverlayVisibility={toggleOverlayVisibility}
                useSimplifiedLayout={useSimplifiedLayout}
                businessUnit={businessUnit}
                accountManager={accountManager}
                hasAppError={hasAppError}
                canViewBillingIngformation={businessUnit.access.permissions.includes("billing_view_billinginfo")}
                onboardingNotification={onboardingNotification}
                onboardingNotificationsScheduleEnabled={onboardingNotificationsScheduleEnabled}
              />
              <BurgerMenu
                expandMenuOnMobile={expandMenuOnMobile}
                useSimplifiedLayout={useSimplifiedLayout}
                setShowSubMenu={setShowSubMenu}
                showSubMenu={showSubMenu}
              />
            </StyledTopbar>

            {!useSimplifiedLayout && (
              <Sidebar
                isMenuExpandOnMobile={isMenuExpandOnMobile}
                hasAppError={hasAppError}
                expandMenuOnMobile={expandMenuOnMobile}
                showSubMenu={showSubMenu}
                setShowSubMenu={setShowSubMenu}
                tier={tier}
                trial={trial}
                freeTrialDashboardEnabled={freeTrialDashboardEnabled}
                locale={locale as Locales}
                eligibility={eligibility as EligibleForUpgradeResponseBody}
                setShowFreeTrialModal={setShowFreeTrialModal}
                autoRenew={businessUnit.planDetails?.autoRenew}
                setIsMenuExpandOnMobile={setIsMenuExpandOnMobile}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                navItems={navItems}
                setNavItems={setNavItems}
                newActiveItem={newActiveItem}
                menu={menu}
                reviewInsightsVisible={reviewInsightsVisible}
                fetchedShellData={fetchedShellData}
              />
            )}
            <SalesforceChat
              businessUser={businessUser}
              businessUnit={businessUnit}
              salesforceId={salesforceId}
              locale={locale}
            />
            <Simplicity />
            <CrowdinInContext />
          </React.Fragment>
        </StyledContent>
      </StyledWrapper>
      <AccountSelector allAccounts={businessAccounts} onCloseAccountSelector={onCloseAccountSelector} />
      {showContentIntegrityMessages && (
        <ContentIntegrityMessages
          userName={businessUser.name}
          userEmail={businessUser.email}
          employeePermissions={businessUser.privileges}
          businessUnit={businessUnit}
          messages={complianceNotifications}
          launchDarkly={launchDarkly}
        />
      )}
      {unpaidInvoiceInfo && <UnpaidInvoiceMessages unpaidInvoiceInfo={unpaidInvoiceInfo} billingInfo={billingInfo} />}
      {showFreeTrialModal && (
        <ConstellationProvider locale={locale as Locales}>
          <FreeTrialModal
            open={showFreeTrialModal}
            onClose={(closeMenu = false) => {
              if (closeMenu) {
                expandMenuOnMobile();
              }
              setShowFreeTrialModal(false);
              analytics.track(trackingConst.events.buttonClicked, {
                context: trackingConst.contexts.freeTrialModal,
                name: trackingConst.names.freeTrialClosed,
              });
              setTimeout(() => {
                const openModalButton = document.getElementById("open-modal-button");
                if (openModalButton) openModalButton.focus();
              }, 100);
            }}
            duration={eligibility.trial.duration}
          />
        </ConstellationProvider>
      )}
    </>
  );
};

export default Shell;
